import { ReactNode } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';

import {
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Theme,
  useTheme,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';

const useStyles = makeStyles<{
  maxWidth?: string;
  selectValue?: string;
  disabled?: boolean;
  minWidth?: string;
  width?: string;
}>()((theme: Theme, { maxWidth, selectValue, disabled, minWidth, width }) => ({
  defaultSelect: {
    ...(maxWidth && { maxWidth }),
    ...(minWidth && { minWidth }),
    ...(width && { width }),
    margin: 0,
    justifyContent: 'flex-end',
    background: disabled
      ? 'rgba(255, 255, 255, 0.04)'
      : 'rgba(255, 255, 255, 0.03)',
    borderRadius: '12px',

    ul: {
      li: {
        fontSize: '14px',
        fontWeight: 500,
        fontFamily: 'Inter',
      },
    },

    '@media (max-width: 550px)': {
      width: '100%',
    },

    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.secondary.dark,
      borderRadius: '12px',
    },

    '& .MuiSelect-select': {
      '&:focus': {
        backgroundColor: 'transparent',
      },
    },

    '& .MuiOutlinedInput-input': {
      color: theme.palette.common.white,
      padding: '12px 16px',
      borderRadius: '12px',

      '&.Mui-disabled': {
        color: theme.palette.text.disabled,
        WebkitTextFillColor: theme.palette.text.disabled,
      },
    },

    '& .MuiOutlinedInput-root': {
      '&:hover': {
        border: 'none',

        '&.Mui-disabled': {
          color: theme.palette.text.disabled,

          '& .MuiOutlinedInput-notchedOutline': {
            borderWidth: 0,
            borderColor: 'transparent',
          },
        },

        '& .MuiOutlinedInput-notchedOutline': {
          borderWidth: '2px',
          borderColor: theme.palette.info.main,
        },
      },

      '&.Mui-focused': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderWidth: '2px',
          borderColor: theme.palette.info.main,
        },
      },

      '&.Mui-disabled': {
        color: theme.palette.text.disabled,
      },
    },

    '& .MuiInputLabel-root[data-shrink="false"]': {
      color: theme.palette.text.disabled,
      top: '3px',
    },

    '& .MuiInputLabel-root[data-shrink="true"]': {
      color: theme.palette.text.disabled,

      '&.Mui-focused': {
        color: theme.palette.text.disabled,
      },
    },

    '& .MuiSelect-icon': {
      color: selectValue
        ? theme.palette.common.white
        : theme.palette.text.disabled,

      '&.Mui-disabled': {
        color: theme.palette.text.disabled,
      },
    },
  },

  dropdownMenuItem: {
    fontSize: '14px',
    fontWeight: 500,
    fontFamily: 'Inter',
  },
}));

export interface FormSelectOption {
  id: string;
  label: string;
}

interface FormSelectFieldProps<T extends FormSelectOption = FormSelectOption> {
  className?: string;
  name: string;
  label: string;
  options: T[];
  readOnly?: boolean;
  maxWidth?: string;
  disabled?: boolean;
  required?: boolean;
  minWidth?: string;
  width?: string;
  customOnChange?: (value: string, name: string) => void;
  handleChange?: (value: string, name: string) => void;
  resetFields?: () => void;
  shouldClearable?: boolean;
  renderOption?: (
    option: T,
    selected: boolean,
  ) => ReactNode;
  renderSelectedValue?: (
    option: T,
  ) => ReactNode;
}

const FormSelectField = <T extends FormSelectOption = FormSelectOption>({
  className,
  name,
  label,
  options,
  maxWidth,
  disabled,
  readOnly,
  minWidth,
  width,
  customOnChange,
  resetFields,
  handleChange,
  shouldClearable,
  renderOption,
  renderSelectedValue,
}: FormSelectFieldProps<T>) => {
  const theme = useTheme();

  const { control, setValue, watch } = useFormContext();

  const selectValue = watch(name);

  const styles = useStyles({
    maxWidth,
    selectValue,
    disabled,
    minWidth,
    width,
  });

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(name, event.target.value, {
      shouldDirty: true,
      shouldValidate: true,
    });

    handleChange?.(event.target.value, name);
  };

  const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    setValue(name, '', {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });

    handleChange?.('', name);
  };

  return (
    <>
      {options && (
        <Controller
          name={name}
          control={control}
          render={({ field: { value } }) => (
            <FormControl
              className={clsx(styles.classes.defaultSelect, className)}
              size="small"
              sx={{ m: 1, minWidth: maxWidth }}
              disabled={disabled}
            >
              <InputLabel>{label}</InputLabel>

              <Select
                label={label}
                value={value ?? ''}
                onChange={(event) => {
                  resetFields?.();

                  customOnChange
                    ? customOnChange(event.target.value as string, name)
                    : onChange(
                      event as React.ChangeEvent<HTMLInputElement>,
                    );
                }}
                renderValue={(selectedValue) => {
                  const selectedOption = options.find(
                    (option) =>
                      String(option.id) === String(selectedValue),
                  );

                  if (!selectedOption) {
                    return '';
                  }

                  return renderSelectedValue
                    ? renderSelectedValue(selectedOption)
                    : selectedOption.label;
                }}
                disabled={disabled}
                readOnly={readOnly}
                {...(shouldClearable &&
                  !disabled &&
                  !readOnly && {
                  IconComponent: value
                    ? () => (
                      <IconButton
                        onMouseDown={(event) =>
                          event.stopPropagation()
                        }
                        onClick={handleClear}
                        disableRipple
                        sx={{
                          color: theme.palette.common.white,

                          '& svg': {
                            fontSize: '20px',
                          },
                        }}
                      >
                        <ClearIcon />
                      </IconButton>
                    )
                    : undefined,
                })}
                MenuProps={{
                  MenuListProps: {
                    sx: {
                      backgroundColor: theme.palette.secondary.dark,
                    },
                  },
                }}
              >
                {options.map((option) => {
                  const selected =
                    String(value) === String(option.id);

                  return (
                    <MenuItem
                      className={styles.classes.dropdownMenuItem}
                      key={`${option.id}-${option.label}`}
                      value={option.id}
                    >
                      {renderOption
                        ? renderOption(option, selected)
                        : option.label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}
        />
      )}
    </>
  );
};

export default FormSelectField;